import React from "react"
import {useParams} from "react-router-dom"
import Bills, {BillsColumns} from "../../components/Bills/Bills"
import {VendorDetails, VendorPaymentMethods} from "../../components/Bills/VendorDetails"
import {KeyValueCardPending} from "../../components/KeyValueCard/KeyValueCard"
import TitleBar, {MetaItemProps} from "../../components/TitleBar/TitleBar"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import MainSectionLayout from "../../containers/MainSection/MainSectionLayout"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {getVendor, getVendorName, VendorDetailInclude} from "../../resources/bills"
import {Customer, CustomerResourceTypes, getCustomerName} from "../../resources/customer"
import {Org, orgType} from "../../resources/org"
import {useAccessToken, useIsBankUser, useIsUnitUser} from "../../services/auth"

export default function VendorPage() {
	const accessToken = useAccessToken()
	const {vendorId = ""} = useParams<{vendorId: string}>()
	const [refreshToken] = useRefreshToken()
	const vendorAsyncResult = useAsyncResult(
		() => getVendor(accessToken, vendorId, VendorDetailInclude),
		[refreshToken, vendorId]
	)
	const showOrgColumn = useIsBankUser() || useIsUnitUser()

	return (
		<div className="bill-full-page">
			<AsyncResultComponent
				asyncResult={vendorAsyncResult}
				pendingComponent={
					<>
						<TitleBar title={`Vendor #${vendorId}`} breadcrumbs={[{text: "Bills", path: "/bills"}]} />
						<MainSectionLayout fullWidth={true}>
							<div className="columns">
								<div className="column is-6">
									<KeyValueCardPending rows={6} />
								</div>
								<div className="column is-6">
									<KeyValueCardPending rows={6} />
								</div>
							</div>
						</MainSectionLayout>
					</>
				}
			>
				{({value: vendorResponse}) => {
					const vendor = vendorResponse.data
					const included = vendorResponse.included ?? []
					const customerId = vendor.relationships?.customer?.data.id
					const customer = included.find(
						(r) => CustomerResourceTypes.includes(r.type) && r.id === customerId?.toString()
					) as Customer | undefined
					const org = included.find((r) => r.type === orgType) as Org | undefined
					const vendorName = getVendorName(vendor)

					const meta: MetaItemProps[] = [
						...(org
							? [
									{
										text: org.attributes.name,
										icon: "man-business-suitcase---users",
										path: `/orgs/${org.id}`,
									} as MetaItemProps,
							  ]
							: []),
						...(customerId
							? [
									{
										text: customer ? getCustomerName(customer) : "Customer",
										icon: "user-geometric-action-money-fund---users",
										path: `/customer/${customerId}`,
									} as MetaItemProps,
							  ]
							: []),
					]

					return (
						<>
							<TitleBar
								title={vendorName || `Vendor #${vendorId}`}
								breadcrumbs={[{text: "Bills", path: "/bills"}]}
								meta={meta}
							/>
							<MainSectionLayout fullWidth={true}>
								<div className="columns vendor-info-columns">
									<div className="column is-6">
										<VendorDetails vendor={vendor} />
									</div>
									<div className="column is-6">
										<VendorPaymentMethods vendor={vendor} />
									</div>
								</div>
								<div className="columns">
									<div className="column">
										<Bills
											vendorId={vendorId}
											enableTitle={true}
											fullHeight={false}
											enableStatusFilter={true}
											enableDateFilter={true}
											includedColumns={[
												BillsColumns.id,
												...(showOrgColumn ? [BillsColumns.org] : []),
												BillsColumns.customer,
												BillsColumns.invoiceNumber,
												BillsColumns.amount,
												BillsColumns.status,
												BillsColumns.dueDate,
												BillsColumns.createdAt,
											]}
										/>
									</div>
								</div>
							</MainSectionLayout>
						</>
					)
				}}
			</AsyncResultComponent>
		</div>
	)
}
